import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/auth";
import { Button, Field } from "@/ui";

export default function Recover() {
  const navigate = useNavigate();
  const { forgotPassword, resetPassword } = useAuth();
  // Read the emailed token once at mount, then scrub it from the address bar (below).
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  useEffect(() => {
    // Scrub the token from the URL so it isn't shoulder-surfed, saved in history, or leaked via
    // the Referer header; and keep this page from leaking any referrer to third parties.
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    if (token) window.history.replaceState({}, "", "/recover");
    return () => { document.head.removeChild(meta); };
  }, [token]);
  const [stage, setStage] = useState(token ? "reset" : "email"); // email | sent | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const requestReset = async () => {
    setErr("");
    if (!email.includes("@")) return setErr("Enter the email on your account.");
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      // Always show the same confirmation — we never reveal whether an account exists.
      setStage("sent");
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const doReset = async () => {
    setErr("");
    if (password.length < 6) return setErr("Password must be at least 6 characters.");
    setLoading(true);
    try {
      await resetPassword(token, password);
      navigate("/onboarding");
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md flex flex-col gap-5">
        <button className="flex items-center gap-1 text-sm text-muted-foreground" onClick={() => navigate("/onboarding")}>
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="text-3xl font-semibold tracking-tight">Reset your password</h1>
        {stage === "email" ? (
          <>
            <p className="text-sm text-muted-foreground">
              Enter your account email and we'll send you a link to reset your password.
            </p>
            <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" data-testid="reset-email" />
            {err ? <p data-testid="reset-error" className="text-sm font-semibold">{err}</p> : null}
            <Button data-testid="reset-request" onClick={requestReset} loading={loading}>Send reset link</Button>
          </>
        ) : stage === "sent" ? (
          <>
            <p className="text-sm text-muted-foreground" data-testid="reset-sent">
              If an account exists for <span className="font-semibold text-foreground">{email}</span>, we've emailed a link to reset your password. It expires in 1 hour. Check your inbox (and spam folder).
            </p>
            <Button data-testid="reset-back" onClick={() => navigate("/onboarding")}>Back to sign in</Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">Choose a new password for your account.</p>
            <Field label="New password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="reset-password" />
            {err ? <p data-testid="reset-error" className="text-sm font-semibold">{err}</p> : null}
            <Button data-testid="reset-submit" onClick={doReset} loading={loading}>Set new password</Button>
          </>
        )}
      </div>
    </div>
  );
}
